import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Check, Loader2, Copy, Gift, Users, ArrowLeft, Zap, Crown, Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ReferralDashboard } from "../components/ReferralDashboard";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLANS = [
  {
    key: "free", name: "Free", price: { month: "$0", year: "$0" }, period: { month: "forever", year: "forever" }, icon: Sparkles, accent: "#64748B",
    tagline: "Great to get started", lookup: null,
    features: ["Up to 5 stored authorizations", "+5 storage per referral", "Draw & typed e-signatures", "Secure signing links & PDF export"],
  },
  {
    key: "growth", name: "Growth", price: { month: "$5", year: "$50" }, period: { month: "/month", year: "/year" }, icon: Zap, accent: "#2563EB",
    tagline: "For growing businesses", lookup: { month: "growth_monthly", year: "growth_yearly" },
    features: ["Up to 50 stored authorizations", "Everything in Free", "Email signing links", "Audit certificates on every form"],
  },
  {
    key: "pro", name: "Pro", price: { month: "$10", year: "$100" }, period: { month: "/month", year: "/year" }, icon: Crown, accent: "#059669",
    tagline: "Unlimited scale", lookup: { month: "pro_monthly", year: "pro_yearly" },
    features: ["Unlimited stored authorizations", "Everything in Growth", "Priority storage", "Best for high volume"],
  },
];

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");
  const [interval, setInterval] = useState("month");

  const load = async () => {
    try {
      const res = await api.get("/subscription");
      setSub(res.data);
      if (res.data.plan !== "free") setInterval(res.data.billing_interval || "month");
    } catch (e) {
      toast.error("Failed to load billing");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const referralUrl = sub?.referral_code ? `${window.location.origin}/?ref=${sub.referral_code}` : "";
  const copyRef = () => {
    navigator.clipboard.writeText(referralUrl);
    toast.success("Referral link copied — share it to earn +5 storage each!");
  };

  const isCurrent = (plan) => plan.key === sub.plan && (plan.key === "free" || (sub.billing_interval || "month") === interval);

  const choose = async (plan) => {
    if (isCurrent(plan)) return;
    const lookup = plan.lookup ? plan.lookup[interval] : null;
    setActing(plan.key);
    try {
      if (plan.key === "free") {
        await api.post("/subscription/cancel");
        await refreshUser();
        toast.success("Subscription cancelled — you're back on the Free plan");
        load();
      } else if (sub.subscription_status === "active" && sub.plan !== "free") {
        await api.post("/subscription/change", { lookup_key: lookup });
        await refreshUser();
        toast.success(`Switched to ${plan.name} (${interval === "year" ? "yearly" : "monthly"})`);
        load();
      } else {
        const res = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setActing("");
    }
  };

  const usagePct = sub && sub.limit ? Math.min(100, Math.round((sub.used / sub.limit) * 100)) : (sub?.plan === "pro" ? 12 : 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#0F172A] mb-5">
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F172A]">Plans &amp; billing</h1>
        <p className="text-sm text-[#64748B] mt-1">Upgrade to store and collect more authorizations. Refer businesses to earn free storage.</p>

        {loading || !sub ? (
          <div className="p-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E2E8F0] border-t-[#2563EB] animate-spin" /></div>
        ) : (
          <>
            {/* Usage + referral */}
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="usage-card">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Current usage</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6FF] px-2.5 py-1 text-xs font-bold text-[#2563EB] capitalize">
                    <ShieldCheck size={12} /> {sub.plan} plan
                  </span>
                </div>
                <p className="mt-3 text-3xl font-extrabold font-mono text-[#0F172A]">
                  {sub.used}<span className="text-lg text-[#94A3B8]"> / {sub.limit ?? "∞"}</span>
                </p>
                <p className="text-xs text-[#64748B]">authorizations stored</p>
                {sub.limit && (
                  <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${usagePct}%`, background: usagePct >= 100 ? "#D97706" : "#2563EB" }} />
                  </div>
                )}
                {sub.limit && sub.used >= sub.limit && (
                  <p className="mt-2 text-xs font-semibold text-[#D97706]">You've hit your limit — sending &amp; signing are paused until you upgrade.</p>
                )}
              </div>

              <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="referral-card">
                <div className="flex items-center gap-2">
                  <Gift size={16} className="text-[#059669]" />
                  <span className="text-sm font-bold text-[#0F172A]">Refer &amp; earn</span>
                </div>
                <p className="text-xs text-[#64748B] mt-1">Share your link. You and the business you refer each get <strong className="text-[#059669]">+5 storage</strong> on signup.</p>
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-slate-50 px-3 py-2">
                  <span className="truncate text-xs font-mono text-[#475569]" data-testid="referral-link-text">{referralUrl}</span>
                </div>
                <button data-testid="copy-referral-btn" onClick={copyRef} className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#059669] py-2 text-sm font-semibold text-white hover:bg-[#047857] transition-colors">
                  <Copy size={14} /> Copy referral link
                </button>
                <div className="mt-3 flex items-center gap-4 text-xs text-[#64748B]">
                  <span className="inline-flex items-center gap-1.5"><Users size={13} /> {sub.referrals} referred</span>
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={13} /> +{sub.bonus_slots} bonus storage</span>
                </div>
              </div>
            </div>

            {/* Plans */}
            {sub.subscription_status === "past_due" && (
              <div data-testid="billing-past-due-notice" className="mt-6 rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#B91C1C]">
                <strong>Payment failed.</strong> Stripe is retrying your card. Re-subscribe below with a working card to restore billing, or you'll return to Free once retries stop.
              </div>
            )}
            {sub.is_team_member && (
              <div data-testid="billing-member-notice" className="mt-6 rounded-xl border border-[#BAE6FD] bg-[#F0F9FF] p-4 text-sm text-[#0284C7]">
                You're a team member of <strong>{sub.vault_name}</strong>. Plan and capacity are managed by the vault owner.
              </div>
            )}
            <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-base md:text-lg font-bold text-[#0F172A]">Plans</h2>
              <div className="inline-flex items-center rounded-lg bg-slate-100 p-1" data-testid="billing-interval-toggle">
                {["month", "year"].map((iv) => (
                  <button key={iv} data-testid={`billing-interval-${iv}`} onClick={() => setInterval(iv)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${interval === iv ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B]"}`}>
                    {iv === "month" ? "Monthly" : "Yearly"}
                    {iv === "year" && <span className="ml-1.5 rounded-full bg-[#ECFDF5] px-1.5 py-0.5 text-[10px] font-bold text-[#059669]">2 months free</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {PLANS.map((plan) => {
                const current = isCurrent(plan);
                const Icon = plan.icon;
                let label = "Upgrade";
                if (current) label = "Current plan";
                else if (plan.key === "free") label = "Cancel subscription";
                else if (sub.subscription_status === "active" && sub.plan !== "free") label = `Switch to ${plan.name}${plan.key === sub.plan ? (interval === "year" ? " yearly" : " monthly") : ""}`;
                return (
                  <div key={plan.key} data-testid={`plan-card-${plan.key}`} className={`rounded-xl border bg-white p-6 flex flex-col ${current ? "border-[#2563EB] ring-2 ring-[#2563EB]/15" : "border-[#E2E8F0]"}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${plan.accent}14` }}>
                        <Icon size={18} style={{ color: plan.accent }} />
                      </span>
                      <div>
                        <p className="font-bold text-[#0F172A]">{plan.name}</p>
                        <p className="text-xs text-[#94A3B8]">{plan.tagline}</p>
                      </div>
                    </div>
                    <p className="mt-4"><span className="text-3xl font-extrabold text-[#0F172A]" data-testid={`plan-price-${plan.key}`}>{plan.price[interval]}</span><span className="text-sm text-[#64748B]">{plan.period[interval]}</span></p>
                    {interval === "year" && plan.key !== "free" && <p className="text-xs text-[#059669] font-semibold mt-0.5">Save {plan.key === "growth" ? "$10" : "$20"} vs monthly</p>}
                    <ul className="mt-4 space-y-2 flex-1">
                      {plan.features.map((ft, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#334155]">
                          <Check size={15} className="mt-0.5 shrink-0" style={{ color: plan.accent }} /> {ft}
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`plan-action-${plan.key}`}
                      disabled={current || acting === plan.key || sub.is_team_member}
                      onClick={() => choose(plan)}
                      className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all active:scale-[0.99] disabled:cursor-default ${
                        current ? "bg-slate-100 text-[#94A3B8]" : plan.key === "free" ? "border border-[#E2E8F0] text-[#DC2626] hover:bg-red-50" : "text-white"
                      }`}
                      style={!current && plan.key !== "free" ? { background: plan.accent } : {}}
                    >
                      {acting === plan.key && <Loader2 size={15} className="animate-spin" />}
                      {label}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-center text-xs text-[#94A3B8]">Test mode — use card 4242 4242 4242 4242, any future expiry &amp; CVC.</p>
            <ReferralDashboard />
          </>
        )}
      </main>
    </div>
  );
}
